import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getMoviesByCategory, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [displayMovies, setDisplayMovies] = useState<Movie[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  useEffect(() => {
    setActiveSlug(slug || 'all');
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  useEffect(() => {
    let filtered = getMoviesByCategory(activeSlug);
    if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      filtered = [...filtered].sort((a, b) => b.viewCount - a.viewCount);
    } else {
      filtered = [...filtered].sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
    }
    setDisplayMovies(filtered);
  }, [activeSlug, sortBy]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部影片';

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 97影院`,
    description: `97影院${categoryName}专区，高清${categoryName}在线观看，超清蓝光画质不缓冲流畅播放。`,
    url: `https://www.97cinema.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: displayMovies.map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://www.97cinema.com/article/${movie.id}`,
          image: movie.posterImage,
          dateCreated: movie.releaseDate,
          director: { '@type': 'Person', name: movie.director },
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: '影视库', href: '/category/all' },
                    { label: categoryName },
                  ]}
                />
                <div className="pb-6">
                  <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-1">
                    {categoryName === '全部' ? '影视库' : categoryName}
                  </h1>
                  <p className="text-sm text-foreground-500">
                    共 {displayMovies.length} 部影片
                    {categoryName !== '全部' && ` · 高清${categoryName}在线观看`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序:</span>
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分最高' },
                  { key: 'popular', label: '最受欢迎' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-accent-100 text-accent-700 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {displayMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {displayMovies.map(movie => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-film-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无影片</p>
                  <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors mt-2 inline-block">
                    浏览全部影片
                  </Link>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}